import { User, PaymentRecord } from '../types';

// Calcular días restantes hasta el vencimiento
export const calculateDaysRemaining = (dueDate: Date | null | undefined): number => {
  if (!dueDate || !(dueDate instanceof Date) || isNaN(dueDate.getTime())) {
    return 0; // Si no hay fecha válida, retornar 0 días
  }
  
  const today = new Date();
  const diffTime = dueDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays);
};

// Calcular próxima fecha de vencimiento
export const calculateNextDueDate = (startDate: Date, lastPaymentDate?: Date): Date => {
  const baseDate = lastPaymentDate || startDate;
  const nextDue = new Date(baseDate);
  nextDue.setMonth(nextDue.getMonth() + 1);
  return nextDue;
};

// Obtener estado de notificación basado en días restantes
export const getNotificationType = (daysRemaining: number): 'danger' | 'warning' | 'info' | null => {
  if (daysRemaining === 0) return 'danger';
  if (daysRemaining <= 3) return 'danger';
  if (daysRemaining <= 7) return 'warning';
  if (daysRemaining <= 15) return 'info';
  return null;
};

// Obtener mensaje de notificación
export const getNotificationMessage = (daysRemaining: number): string => {
  if (daysRemaining === 0) return '¡VENCIDO!';
  if (daysRemaining === 1) return 'Vence mañana';
  if (daysRemaining <= 3) return `Vence en ${daysRemaining} días`;
  if (daysRemaining <= 7) return `Vence en ${daysRemaining} días`;
  if (daysRemaining <= 15) return `Vence en ${daysRemaining} días`;
  return '';
};

// Verificar si la membresía está vencida
export const isMembershipExpired = (dueDate: Date | null | undefined): boolean => {
  if (!dueDate || !(dueDate instanceof Date) || isNaN(dueDate.getTime())) {
    return true; // Si no hay fecha válida, considerar como vencida
  }
  return calculateDaysRemaining(dueDate) === 0;
};

// Formatear fecha para mostrar
export const formatDate = (date: Date | null | undefined): string => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return 'No establecida';
  }
  
  return date.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// Crear nuevo registro de pago
export const createPaymentRecord = (
  userId: string,
  amount: number,
  status: 'paid' | 'pending',
  dueDate: Date,
  recordedBy: string,
  notes?: string
): PaymentRecord => {
  const record: PaymentRecord = {
    id: `payment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    userId,
    amount,
    status,
    paymentDate: new Date(),
    dueDate,
    recordedBy,
    createdAt: new Date()
  };

  // Solo incluir notes si no es undefined
  if (notes !== undefined) {
    record.notes = notes;
  }

  return record;
};

// Función para limpiar objetos de valores undefined (Firebase no los acepta)
const cleanObjectForFirebase = (obj: any): any => {
  const cleaned: any = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined && value !== null) {
      if (typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
        cleaned[key] = cleanObjectForFirebase(value);
      } else {
        cleaned[key] = value;
      }
    }
  }
  return cleaned;
};

// Actualizar estado de pago y fechas del usuario
export const updateUserPaymentStatus = (
  user: User,
  newStatus: 'paid' | 'pending',
  adminId: string,
  notes?: string
): Partial<User> => {
  const now = new Date();
  const updates: Partial<User> = {
    paymentStatus: newStatus,
    updatedAt: now
  };

  // Solo incluir lastPaymentDate si es un pago o si ya existe
  if (newStatus === 'paid') {
    updates.lastPaymentDate = now;
  } else if (user.lastPaymentDate) {
    // Si es pending y ya existe lastPaymentDate, mantenerlo
    updates.lastPaymentDate = user.lastPaymentDate;
  }

  // Si es el primer pago, establecer fecha de inicio
  if (newStatus === 'paid' && !user.membershipStartDate) {
    updates.membershipStartDate = now;
  }

  // Calcular próxima fecha de vencimiento solo si es un pago
  if (newStatus === 'paid') {
    const baseDate = user.lastPaymentDate || user.membershipStartDate || now;
    updates.nextDueDate = calculateNextDueDate(baseDate, now);
  }

  // Actualizar historial de pagos
  const newPaymentRecord = createPaymentRecord(
    user.id,
    0, // Amount - se puede ajustar después
    newStatus,
    updates.nextDueDate || user.nextDueDate || now,
    adminId,
    notes
  );

  updates.membershipHistory = [
    ...(user.membershipHistory || []),
    newPaymentRecord
  ];

  // Limpiar el objeto de valores undefined antes de devolverlo
  return cleanObjectForFirebase(updates);
};

// Verificar si el usuario necesita notificación
export const shouldShowNotification = (user: User): boolean => {
  if (!user.nextDueDate || user.membershipType !== 'monthly_fee') {
    return false;
  }

  const daysRemaining = calculateDaysRemaining(user.nextDueDate);
  return daysRemaining <= 15;
}; 